import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { fetchQuotations, deleteQuotation } from "../api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./ListQuotationPage.css";

/**
 * Convert image url -> base64 (PNG).
 */
const getImageBase64 = (url) =>
  new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

const ListQuotationPage = () => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const handleEdit = (id) => navigate(`/edit-quotation/${id}`);

  const loadQuotations = async () => {
    setLoading(true);
    try {
      const data = await fetchQuotations();
      const list = Array.isArray(data)
        ? data
        : data?.data || data?.quotations || [];
      setQuotations(list || []);
    } catch (err) {
      console.error("Error fetching quotations:", err);
      setQuotations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuotations();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to permanently delete this quotation?")) return;
    try {
      await deleteQuotation(id);
      setQuotations((prev) => prev.filter((q) => q.id !== id));
      alert("Quotation deleted successfully.");
    } catch (err) {
      console.error("Error deleting quotation:", err);
      alert("Failed to delete quotation.");
    }
  };

  // ✅ SEND TEXT VIA WHATSAPP
  const handleSendWhatsApp = (quote) => {
    const attrs = quote.attributes || quote;

    const defaultNumber = "9081810100";

    let rawNumber = attrs.contactNo
      ? String(attrs.contactNo).trim()
      : "";

    let finalNumber = "";

    if (rawNumber === "") {
      finalNumber = "+91" + defaultNumber;
    } else if (rawNumber.startsWith("+")) {
      finalNumber = rawNumber;
    } else {
      finalNumber = "+91" + rawNumber;
    }

    finalNumber = finalNumber.replace(/\s+/g, "");

    const message =
      `Hello Sir,\n` +
      `Greetings from Bharat Packs.\n\n` +
      `Thank you for visiting our stall at the ENGIMACH Exhibition.\n` +
      `As discussed, I am sharing the quotation for your reference.\n\n` +
      `Please review the attached “Quote of Reference for You” and feel free to contact me for any clarification or required modifications.\n\n` +
      `We appreciate your interest and look forward to supporting your requirements.`;

    const whatsappURL = `https://wa.me/${finalNumber}?text=${encodeURIComponent(
      message
    )}`;

    window.open(whatsappURL, "_blank");
  };

  // ✅ PDF DOWNLOAD — (UNCHANGED)
  const handleDownload = async (quote) => {
    try {
      const doc = new jsPDF("p", "pt", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 32;
      const usableWidth = pageWidth - margin * 2;
      const attrs = quote.attributes || quote;
      let y = 30;

      const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        if (isNaN(d)) return dateStr;
        return `${String(d.getDate()).padStart(2, "0")}-${String(
          d.getMonth() + 1
        ).padStart(2, "0")}-${d.getFullYear()}`;
      };

      const logo = await getImageBase64("/images/BP_Logo.png");
      if (logo) doc.addImage(logo, "PNG", margin, y, 100, 50);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.setTextColor(0, 0, 0);
      doc.text("BHARAT PACKS", pageWidth - margin, y + 16, { align: "right" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(
        "21, Radhe Krishna Estate, Road No 5, Kathwada GIDC,  Kathwada – 382430",
        pageWidth - margin,
        y + 30,
        { align: "right" }
      );
      doc.text(
        "Ahmedabad, Gujarat, India | +91 90818 10100 | GST: 24ABCFB9318H1ZK",
        pageWidth - margin,
        y + 42,
        { align: "right" }
      );

      // Divider
      y += 60;
      doc.setDrawColor(200, 85, 15);
      doc.setLineWidth(1);
      doc.line(margin, y, pageWidth - margin, y);

      y += 18;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("QUOTATION", pageWidth / 2, y, { align: "center" });

      // Quotation Info
      y += 18;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("Quotation No:", margin, y);
      doc.setFont("helvetica", "normal");
      doc.text(`${attrs.documentNo || ""}`, margin + 80, y);

      doc.setFont("helvetica", "bold");
      doc.text("Date:", pageWidth - margin - 100, y);
      doc.setFont("helvetica", "normal");
      doc.text(`${formatDate(attrs.date)}`, pageWidth - margin - 20, y, {
        align: "right",
      });

      // ✅ ESTIMATE TO
      y += 18;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(200, 85, 15);
      doc.text("Estimate To", margin, y);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(0);

      const wrapText = (text, maxLen = 50) => {
        if (!text) return [];
        const words = text.split(" ");
        const lines = [];
        let line = "";
        for (const w of words) {
          if ((line + w).length <= maxLen) line += (line ? " " : "") + w;
          else {
            lines.push(line);
            line = w;
          }
        }
        if (line) lines.push(line);
        return lines;
      };

      const buyerLines = [
        `${attrs.companyName || ""}`,
        ...wrapText(attrs.address || "", 50),
        `GSTIN: ${attrs.gstin || ""}`,
        `Contact Name: ${attrs.contactName || ""}`,
        `Phone: ${attrs.contactNo || ""}`,
        `Email: ${attrs.contactEmail || ""}`,
      ];

      buyerLines.forEach((t, i) => doc.text(t, margin, y + 13 + i * 11));

      y += 100;

      // ✅ mm formatting
      const formatMM = (val) => (val ? `${val} mm` : "");

      const detailsData = [
        ["Main Category", attrs.mainCategory || "", "Product", attrs.productName || ""],
        ["Handle", attrs.handleType || "", "Case Color", attrs.caseColorType || ""],
        ["Length", formatMM(attrs.caseLength), "Width", formatMM(attrs.caseWidth)],
        ["Top Height", formatMM(attrs.caseTopHeight), "Bottom Height", formatMM(attrs.caseBottomHeight)],
      ];

      // ✅ DETAILS TABLE
      // ✅ DETAILS TABLE
      autoTable(doc, {
        body: detailsData,
        startY: y,
        theme: "grid",
        margin: { left: margin, right: margin },
        tableWidth: usableWidth,
        styles: {
          fontSize: 9.5,
          cellPadding: 3,
          lineColor: [0, 0, 0],
          textColor: [0, 0, 0],
        },
        columnStyles: {
          0: { cellWidth: usableWidth * 0.2 },
          1: { cellWidth: usableWidth * 0.3 },
          2: { cellWidth: usableWidth * 0.2 },
          3: { cellWidth: usableWidth * 0.3 },
        },
      });

      y = doc.lastAutoTable.finalY + 6;

      // ✅ DEFINE COLUMNS & ROWS BEFORE USAGE
      const itemColumns = ["#", "Item Description", "Quantity", "Unit", "Price/Unit", "Amount"];
      const itemRows = (attrs.items || []).map((i, idx) => [
        idx + 1,
        i.name || "",
        i.quantity ? Number(i.quantity).toFixed(3).replace(/\.?0+$/, "") : "0",
        i.unit || "",
        `${Number(i.price || 0).toFixed(2)}`,
        `${Number(i.amount || 0).toFixed(2)}`,
      ]);

      // ✅ SINGLE CLEAN ITEM TABLE
      autoTable(doc, {
        head: [itemColumns],
        body: itemRows.length ? itemRows : [["", "", "", "", "", ""]],
        startY: y,
        theme: "grid",
        tableWidth: usableWidth,
        margin: { left: margin, right: margin },

        headStyles: {
          fillColor: [200, 85, 15],
          textColor: [0, 0, 0],
          fontStyle: "bold",
          fontSize: 9.5,
          halign: "center"
        },

        styles: {
          fontSize: 8.5,
          cellPadding: 3,
          lineColor: [0, 0, 0],
          textColor: [0, 0, 0]
        },

        // ✅ UPDATED COLUMN WIDTH + RIGHT ALIGN QUANTITY
        columnStyles: {
          0: { cellWidth: usableWidth * 0.06 }, // #
          1: { cellWidth: usableWidth * 0.52 }, // Item Description
          2: { cellWidth: usableWidth * 0.09, halign: "right" }, // ✅ RIGHT ALIGN
          3: { cellWidth: usableWidth * 0.08 }, // Unit
          4: { cellWidth: usableWidth * 0.13 }, // Price/Unit
          5: { cellWidth: usableWidth * 0.13 }  // Amount
        }
      });

      y = doc.lastAutoTable.finalY + 6;

      const total = (attrs.items || []).reduce(
        (sum, i) => sum + Number(i.amount || 0),
        0
      );
      const caseQuantity = Number(attrs.caseQuantity || 0);
      const subtotal = total * caseQuantity;
      const gst = subtotal * 0.18;
      const grandTotal = subtotal + gst;

      const totals = [
        ["Per Case Price", total.toFixed(2)],
        ["Quantity", caseQuantity.toFixed(2)],
        ["Taxable Amount", subtotal.toFixed(2)],
        ["GST (18%)", gst.toFixed(2)],
        ["Grand Total", grandTotal.toFixed(2)],
      ];

      const boxWidth = usableWidth * 0.35;
      const boxX = pageWidth - margin - boxWidth;

      totals.forEach(([label, val], i) => {
        const rowY = y + i * 18;
        doc.setDrawColor(200, 85, 15);
        doc.rect(boxX, rowY, boxWidth, 18);
        doc.setFontSize(9.5);
        doc.text(label, boxX + 6, rowY + 12);
        doc.text(val, boxX + boxWidth - 6, rowY + 12, { align: "right" });
      });

      // ✅ AFTER TOTALS BOX
      y = doc.lastAutoTable.finalY + 15;

      // ✅ BANK DETAILS
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("Bank Details:", margin, y);

      const bankLines = [
        "Bank Name: HDFC BANK, AHMEDABAD - MANINAGAR",
        "Account No: 50200096984600",
        "IFSC Code: HDFC0000300",
        "Account  holder's Name: BHARAT PACKS",

      ];

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      bankLines.forEach((t, i) => doc.text(t, margin + 10, y + 12 + i * 10));

      y += bankLines.length * 10 + 30;

      // ✅ TERMS & CONDITIONS
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("Terms & Conditions:", margin, y);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      const terms = [
        "1. All payments to be made in favour of BHARAT PACKS",
        "2. Payment Terms: 100% Advance.",
        "3. Delivery: Within 20 days from receipt of advance payment.",
        "4. Quotation Validity: 30 Days.",
        "5. Freight Charges: Extra / To Pay Basis.",
        "6. Sampling Charges: Rs.2000 per unit (up to 5 units).",
      ];
      terms.forEach((t, i) => doc.text(t, margin + 10, y + 12 + i * 10));

      // ✅ SIGNATURE
      y += 45; // small spacing after terms

      const signY = y;
      const sign = await getImageBase64("/images/Signature.jpg");
      if (sign) doc.addImage(sign, "PNG", pageWidth - margin - 105, signY, 95, 38);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.text("For BHARAT PACKS", pageWidth - margin - 25, signY + 48, { align: "right" });


      y = signY + 72;


      // ✅ FOOTER
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(
        "Phone: +91 90818 10100   |   Email: sales@bharatpacks.com   |   Website: www.bharatpacks.com",
        pageWidth / 2,
        pageHeight - 18,
        { align: "center" }
      );

      doc.save(`Quotation_${attrs.documentNo || "BP"}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
      alert("Failed to generate PDF.");
    }
  };

  if (loading) return <p>Loading quotations...</p>;
  if (!quotations.length) return <p>No quotations found.</p>;

  return (
    <>
      <Header />
      <div className="list-quotation-container">
        <h1>All Quotations</h1>
        <table className="quotation-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Document No</th>
              <th>Company Name</th>
              <th>Product Name</th>
              <th>Grand Total</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {quotations.map((quote) => {
              const attrs = quote.attributes || quote;

              const totalValue = attrs.grandTotal
                ? Number(attrs.grandTotal).toFixed(2)
                : "0.00";

              return (
                <tr key={quote.id}>
                  <td>{attrs.date || "-"}</td>
                  <td>{attrs.documentNo || "-"}</td>
                  <td>{attrs.companyName || "-"}</td>
                  <td>{attrs.productName || "-"}</td>

                  {/* ✅ SHOW MULTIPLIER IN SAME COLUMN */}
                  <td>
                    {totalValue}
                    {attrs.multiplier ? ` (${attrs.multiplier}x)` : ""}
                  </td>

                  <td>
                    <button onClick={() => handleDownload(quote)}>PDF</button>
                    <button onClick={() => handleSendWhatsApp(quote)}>Send</button>
                    <button onClick={() => handleEdit(quote.id)}>Edit</button>
                    <button onClick={() => handleDelete(quote.id)}>Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>

        </table>
      </div>
    </>
  );
};

export default ListQuotationPage;
